import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiCheckSquare, FiLink, FiSearch, FiSquare, FiTrash2 } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const LEVEL_OPTIONS = [
  { value: "family", label: "Family" },
  { value: "subfamily", label: "Subfamily" },
  { value: "category", label: "Category" },
  { value: "subcategory", label: "Subcategory" },
];

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function pluralLevel(level) {
  if (level === "family") return "families";
  if (level === "subfamily") return "subfamilies";
  if (level === "category") return "categories";
  if (level === "subcategory") return "subcategories";
  return `${level}s`;
}

function mappingKey(level, id) {
  return `${level}:${id}`;
}

function compactNumber(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  const rounded = Math.round(number);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

function nodeLabel(node) {
  return node?.label || `${node?.code || node?.id || ""} - ${node?.name || ""}`.trim();
}

function draftFromMapping(mapping) {
  return {
    target_level: mapping?.target_level || "family",
    target_id: mapping?.target_id ? String(mapping.target_id) : "",
  };
}

export default function AnalyticsSettings() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [data, setData] = useState(null);
  const [sourceLevel, setSourceLevel] = useState("family");
  const [searchQuery, setSearchQuery] = useState("");
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadMappings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/analytics/taxonomy-mappings", {
        params: { business_unit_id: businessUnitId },
      });
      setData(res.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load taxonomy mappings.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const mappingsBySource = useMemo(() => {
    const map = new Map();
    (data?.mappings || []).forEach((mapping) => {
      map.set(mappingKey(mapping.source_level, mapping.source_id), mapping);
    });
    return map;
  }, [data?.mappings]);

  useEffect(() => {
    const nextDrafts = {};
    (data?.mappings || []).forEach((mapping) => {
      nextDrafts[mappingKey(mapping.source_level, mapping.source_id)] = draftFromMapping(mapping);
    });
    setDrafts(nextDrafts);
  }, [data?.mappings]);

  const sourceRows = useMemo(() => {
    const rows = data?.taxonomy?.pos?.[pluralLevel(sourceLevel)] || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      [row.code, row.name, row.label, row.path].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [data?.taxonomy?.pos, searchQuery, sourceLevel]);

  const targetOptionsByLevel = useMemo(() => {
    return LEVEL_OPTIONS.reduce((acc, level) => {
      acc[level.value] = data?.taxonomy?.purchasing?.[pluralLevel(level.value)] || [];
      return acc;
    }, {});
  }, [data?.taxonomy?.purchasing]);

  const updateDraft = (key, patch) => {
    setDrafts((current) => ({
      ...current,
      [key]: {
        target_level: "family",
        target_id: "",
        ...(current[key] || {}),
        ...patch,
      },
    }));
  };

  const saveMapping = async (source, draft) => {
    const key = mappingKey(sourceLevel, source.id);
    if (!draft?.target_level || !draft?.target_id) return;

    try {
      setSavingKey(key);
      setError("");
      setMessage("");
      const res = await axios.put("/api/analytics/taxonomy-mappings", {
        business_unit_id: businessUnitId,
        source_level: sourceLevel,
        source_id: source.id,
        target_level: draft.target_level,
        target_id: draft.target_id,
      });
      setData(res.data || null);
      setMessage("Mapping saved.");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save taxonomy mapping.");
    } finally {
      setSavingKey("");
    }
  };

  const clearMapping = async (source) => {
    const key = mappingKey(sourceLevel, source.id);
    try {
      setSavingKey(key);
      setError("");
      setMessage("");
      const res = await axios.delete("/api/analytics/taxonomy-mappings", {
        params: {
          business_unit_id: businessUnitId,
          source_level: sourceLevel,
          source_id: source.id,
        },
      });
      setData(res.data || null);
      setMessage("Mapping cleared.");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not clear taxonomy mapping.");
    } finally {
      setSavingKey("");
    }
  };

  const summary = data?.summary || {};

  return (
    <div className="reservations-container rm-page analyticsSettingsPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Analytics settings</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading settings..." : "Cost control mappings"}
            </div>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="posMetricGrid analyticsMetricGrid">
        <div className="posMetricTile">
          <span>POS nodes</span>
          <strong>{compactNumber(summary.pos_nodes)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Mapped</span>
          <strong>{compactNumber(summary.mapped_nodes)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Pending</span>
          <strong>{compactNumber(summary.unmapped_nodes)}</strong>
        </div>
      </div>

      <div className="rm-rates-card analyticsMappingCard analyticsMappingCard--settings">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Taxonomy mapping</h3>
          <div className="rm-rates-subtitle">
            Link POS {pluralLevel(sourceLevel)} to purchasing families, subfamilies, categories or subcategories.
          </div>
        </div>

        <div className="analyticsMappingControls">
          <div className="analyticsLevelChipGrid">
            {LEVEL_OPTIONS.map((level) => {
              const selected = sourceLevel === level.value;
              return (
                <button
                  type="button"
                  key={level.value}
                  className={`analyticsLevelChip ${selected ? "analyticsLevelChip--selected" : ""}`}
                  onClick={() => setSourceLevel(level.value)}
                >
                  {selected ? <FiCheckSquare /> : <FiSquare />}
                  <span>{level.label}</span>
                </button>
              );
            })}
          </div>
          <div className="analyticsSearchBox">
            <FiSearch />
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search POS taxonomy"
            />
          </div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading rows..." : `${sourceRows.length} POS ${pluralLevel(sourceLevel)}`}
          </small>
        </div>

        <div className="rm-rates-table-wrap analyticsMappingWrap">
          <table className="rm-rates-table analyticsMappingTable" style={{ "--rm-col1-width": "300px", "--rm-table-min-width": "1120px" }}>
            <colgroup>
              <col style={{ width: "300px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "360px" }} />
              <col style={{ width: "160px" }} />
              <col style={{ width: "120px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1">
                  <div className="res-thBtn"><span className="res-thLabel">POS source</span><span className="res-thSub">{sourceLevel}</span></div>
                </th>
                <th className="res-th">
                  <div className="res-thBtn"><span className="res-thLabel">Target level</span><span className="res-thSub">Purchasing</span></div>
                </th>
                <th className="res-th">
                  <div className="res-thBtn"><span className="res-thLabel">Purchasing target</span><span className="res-thSub">Cost source</span></div>
                </th>
                <th className="res-th">
                  <div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Mapping</span></div>
                </th>
                <th className="res-th" />
              </tr>
            </thead>
            <tbody>
              {sourceRows.map((source) => {
                const key = mappingKey(sourceLevel, source.id);
                const mapping = mappingsBySource.get(key);
                const draft = drafts[key] || draftFromMapping(mapping);
                const targetOptions = targetOptionsByLevel[draft.target_level] || [];
                const isSaving = savingKey === key;

                return (
                  <tr key={key}>
                    <td className="rm-sticky-1">
                      <div className="analyticsTaxonomySource">
                        <strong>{nodeLabel(source)}</strong>
                        <span>{source.path || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={draft.target_level}
                        disabled={isSaving}
                        onChange={(event) => updateDraft(key, { target_level: event.target.value, target_id: "" })}
                      >
                        {LEVEL_OPTIONS.map((level) => (
                          <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="posTableSelect"
                        value={draft.target_id}
                        disabled={isSaving}
                        onChange={(event) => {
                          const nextDraft = { ...draft, target_id: event.target.value };
                          updateDraft(key, nextDraft);
                          saveMapping(source, nextDraft);
                        }}
                      >
                        <option value="">-</option>
                        {targetOptions.map((target) => (
                          <option key={target.id} value={target.id}>
                            {target.path || nodeLabel(target)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`rm-chip ${mapping ? "rm-chip--ok" : "rm-chip--soft"}`}>
                        {mapping ? (
                          <>
                            <FiCheckCircle /> Mapped
                          </>
                        ) : (
                          <>
                            <FiLink /> Pending
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="posTicketIconBtn"
                        onClick={() => clearMapping(source)}
                        disabled={!mapping || isSaving}
                        title="Clear mapping"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!sourceRows.length ? (
                <tr>
                  <td colSpan="5" className="posMuted">No POS taxonomy rows found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
